import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  FileText,
  BookOpen,
  HelpCircle,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  EyeOff,
  Loader2,
  Check,
  Image,
} from 'lucide-react';
import apiClient from '../../services/api';

const ContentManager = () => {
  const [activeTab, setActiveTab] = useState('blog');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: 'Tips & Guides',
    image: '',
    published: false,
  });
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [postValidationErrors, setPostValidationErrors] = useState({});

  // Pages state
  const [pages, setPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);

  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    category: 'General',
    order: 0,
  });
  const [showNewFaqForm, setShowNewFaqForm] = useState(false);

  const blogCategories = ['Tips & Guides', 'Success Stories', 'Marketing', 'News', 'Updates'];
  const faqCategories = ['General', 'Donations', 'Campaigns', 'Account', 'Payments', 'Trust & Safety'];
  const pagesList = [
    { id: 'about', name: 'About Us' },
    { id: 'careers', name: 'Careers' },
    { id: 'press', name: 'Press' },
    { id: 'partners', name: 'Partners' },
    { id: 'trust', name: 'Trust & Safety' },
    { id: 'pricing', name: 'Pricing' },
    { id: 'guidelines', name: 'Community Guidelines' },
    { id: 'privacy', name: 'Privacy Policy' },
    { id: 'terms', name: 'Terms of Service' },
    { id: 'cookies', name: 'Cookie Policy' },
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [blogRes, pagesRes, faqsRes] = await Promise.all([
        apiClient.get('/content/blog'),
        apiClient.get('/content/pages'),
        apiClient.get('/content/faqs'),
      ]);
      setBlogPosts(blogRes.data.posts || []);
      setPages(pagesRes.data.pages || []);
      setFaqs(faqsRes.data.faqs || []);
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Blog handlers
  const validatePost = () => {
    const errors = {};
    if (!newPost.title?.trim()) errors.title = 'Title is required';
    if (!newPost.author?.trim()) errors.author = 'Author is required';
    if (!newPost.category?.trim()) errors.category = 'Category is required';
    if (!newPost.excerpt?.trim()) errors.excerpt = 'Excerpt is required';
    if (!newPost.content?.trim()) errors.content = 'Content is required';
    setPostValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePost = async () => {
    if (!validatePost()) return;
    setSaving(true);
    try {
      await apiClient.post('/content/blog', newPost);
      setShowNewPostForm(false);
      setNewPost({ title: '', excerpt: '', content: '', author: '', category: 'Tips & Guides', image: '', published: false });
      setPostValidationErrors({});
      fetchContent();
    } catch (err) {
      console.error('Error creating post:', err);
      const detail = err.response?.data?.detail;
      if (detail) {
        alert(typeof detail === 'string' ? detail : 'Failed to create post');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePost = async (postId) => {
    if (!editingPost) return;
    setSaving(true);
    try {
      await apiClient.put(`/content/blog/${postId}`, editingPost);
      setEditingPost(null);
      fetchContent();
    } catch (err) {
      console.error('Error updating post:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await apiClient.delete(`/content/blog/${postId}`);
      fetchContent();
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await apiClient.put(`/content/blog/${post.id}`, { published: !post.published });
      fetchContent();
    } catch (err) {
      console.error('Error toggling publish:', err);
    }
  };

  // Page handlers
  const handleUpdatePage = async (pageId) => {
    if (!editingPage) return;
    setSaving(true);
    try {
      await apiClient.put(`/content/pages/${pageId}`, editingPage);
      setEditingPage(null);
      fetchContent();
    } catch (err) {
      console.error('Error updating page:', err);
    } finally {
      setSaving(false);
    }
  };

  // FAQ handlers
  const handleCreateFaq = async () => {
    if (!newFaq.question || !newFaq.answer) return;
    setSaving(true);
    try {
      await apiClient.post('/content/faqs', newFaq);
      setShowNewFaqForm(false);
      setNewFaq({ question: '', answer: '', category: 'General', order: 0 });
      fetchContent();
    } catch (err) {
      console.error('Error creating FAQ:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFaq = async (faqId) => {
    if (!editingFaq) return;
    setSaving(true);
    try {
      await apiClient.put(`/content/faqs/${faqId}`, editingFaq);
      setEditingFaq(null);
      fetchContent();
    } catch (err) {
      console.error('Error updating FAQ:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await apiClient.delete(`/content/faqs/${faqId}`);
      fetchContent();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Content Management</h2>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="blog" className="data-[state=active]:bg-emerald-500">
            <BookOpen className="w-4 h-4 mr-2" /> Blog
          </TabsTrigger>
          <TabsTrigger value="pages" className="data-[state=active]:bg-emerald-500">
            <FileText className="w-4 h-4 mr-2" /> Pages
          </TabsTrigger>
          <TabsTrigger value="faqs" className="data-[state=active]:bg-emerald-500">
            <HelpCircle className="w-4 h-4 mr-2" /> FAQs
          </TabsTrigger>
        </TabsList>

        {/* Blog Tab */}
        <TabsContent value="blog" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground">{blogPosts.length} posts</p>
            <Button onClick={() => setShowNewPostForm(true)} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </div>

          {/* New Post Form */}
          {showNewPostForm && (
            <Card className="mb-6 border-emerald-500">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>New Blog Post</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewPostForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Input
                    placeholder="Post title *"
                    value={newPost.title}
                    onChange={(e) => { setNewPost({ ...newPost, title: e.target.value }); setPostValidationErrors(prev => ({ ...prev, title: '' })); }}
                    className={postValidationErrors.title ? 'border-red-500' : ''}
                  />
                  {postValidationErrors.title && <p className="text-red-500 text-xs mt-1">{postValidationErrors.title}</p>}
                </div>
                <div>
                  <Input
                    placeholder="Author name *"
                    value={newPost.author}
                    onChange={(e) => { setNewPost({ ...newPost, author: e.target.value }); setPostValidationErrors(prev => ({ ...prev, author: '' })); }}
                    className={postValidationErrors.author ? 'border-red-500' : ''}
                  />
                  {postValidationErrors.author && <p className="text-red-500 text-xs mt-1">{postValidationErrors.author}</p>}
                </div>
                <div>
                  <select
                    className={`w-full px-3 py-2 bg-secondary border rounded-lg ${postValidationErrors.category ? 'border-red-500' : 'border-border'}`}
                    value={newPost.category}
                    onChange={(e) => { setNewPost({ ...newPost, category: e.target.value }); setPostValidationErrors(prev => ({ ...prev, category: '' })); }}
                  >
                    {blogCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {postValidationErrors.category && <p className="text-red-500 text-xs mt-1">{postValidationErrors.category}</p>}
                </div>
                <Input
                  placeholder="Image URL (optional)"
                  value={newPost.image}
                  onChange={(e) => setNewPost({ ...newPost, image: e.target.value })}
                />
                <div>
                  <Textarea
                    placeholder="Short excerpt... *"
                    value={newPost.excerpt}
                    onChange={(e) => { setNewPost({ ...newPost, excerpt: e.target.value }); setPostValidationErrors(prev => ({ ...prev, excerpt: '' })); }}
                    rows={2}
                    className={postValidationErrors.excerpt ? 'border-red-500' : ''}
                  />
                  {postValidationErrors.excerpt && <p className="text-red-500 text-xs mt-1">{postValidationErrors.excerpt}</p>}
                </div>
                <div>
                  <Textarea
                    placeholder="Full content (supports HTML)... *"
                    value={newPost.content}
                    onChange={(e) => { setNewPost({ ...newPost, content: e.target.value }); setPostValidationErrors(prev => ({ ...prev, content: '' })); }}
                    rows={6}
                    className={postValidationErrors.content ? 'border-red-500' : ''}
                  />
                  {postValidationErrors.content && <p className="text-red-500 text-xs mt-1">{postValidationErrors.content}</p>}
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPost.published}
                      onChange={(e) => setNewPost({ ...newPost, published: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span>Publish immediately</span>
                  </label>
                </div>
                <Button onClick={handleCreatePost} disabled={saving} className="w-full bg-emerald-500">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Create Post
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Blog Posts List */}
          <div className="space-y-4">
            {blogPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden">
                <CardContent className="p-4">
                  {editingPost?.id === post.id ? (
                    <div className="space-y-4">
                      <Input
                        value={editingPost.title}
                        onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                      />
                      <Textarea
                        value={editingPost.excerpt}
                        onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                        rows={2}
                      />
                      <Textarea
                        value={editingPost.content}
                        onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                        rows={6}
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => handleUpdatePost(post.id)} disabled={saving} size="sm">
                          <Save className="w-4 h-4 mr-1" /> Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingPost(null)} size="sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        {post.image && (
                          <img src={post.image} alt="" className="w-20 h-20 object-cover rounded" />
                        )}
                        <div>
                          <h3 className="font-semibold text-foreground">{post.title}</h3>
                          <p className="text-sm text-muted-foreground">{post.excerpt}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={post.published ? 'default' : 'secondary'}>
                              {post.published ? 'Published' : 'Draft'}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{post.category}</span>
                            <span className="text-xs text-muted-foreground">by {post.author}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTogglePublish(post)}
                          title={post.published ? 'Unpublish' : 'Publish'}
                        >
                          {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingPost(post)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {blogPosts.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No blog posts yet. Create your first post!</p>
            )}
          </div>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages" className="mt-6">
          <p className="text-muted-foreground mb-4">Edit content for static pages</p>
          <div className="grid gap-4">
            {pagesList.map((page) => {
              const pageContent = pages.find(p => p.page_id === page.id) || { page_id: page.id, title: page.name, content: '' };
              const isEditing = editingPage?.page_id === page.id;

              return (
                <Card key={page.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{page.name}</h3>
                      {!isEditing ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingPage(pageContent)}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdatePage(page.id)} disabled={saving}>
                            <Save className="w-4 h-4 mr-1" /> Save
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEditingPage(null)}>
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-3">
                        <Input
                          placeholder="Page title"
                          value={editingPage.title}
                          onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })}
                        />
                        <Textarea
                          placeholder="Meta description (for SEO)"
                          value={editingPage.meta_description || ''}
                          onChange={(e) => setEditingPage({ ...editingPage, meta_description: e.target.value })}
                          rows={2}
                        />
                        <Textarea
                          placeholder="Page content (supports HTML)..."
                          value={editingPage.content}
                          onChange={(e) => setEditingPage({ ...editingPage, content: e.target.value })}
                          rows={10}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {pageContent.content ? 'Content configured' : 'No custom content - using default'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground">{faqs.length} FAQs</p>
            <Button onClick={() => setShowNewFaqForm(true)} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" /> New FAQ
            </Button>
          </div>

          {/* New FAQ Form */}
          {showNewFaqForm && (
            <Card className="mb-6 border-emerald-500">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>New FAQ</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewFaqForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <select
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                  value={newFaq.category}
                  onChange={(e) => setNewFaq({ ...newFaq, category: e.target.value })}
                >
                  {faqCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <Input
                  placeholder="Question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                />
                <Textarea
                  placeholder="Answer..."
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  rows={4}
                />
                <Input
                  type="number"
                  placeholder="Display order (0 = first)"
                  value={newFaq.order}
                  onChange={(e) => setNewFaq({ ...newFaq, order: parseInt(e.target.value) || 0 })}
                />
                <Button onClick={handleCreateFaq} disabled={saving} className="w-full bg-emerald-500">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Create FAQ
                </Button>
              </CardContent>
            </Card>
          )}

          {/* FAQs List grouped by category */}
          {faqCategories.map(category => {
            const categoryFaqs = faqs.filter(f => f.category === category);
            if (categoryFaqs.length === 0) return null;
            
            return (
              <div key={category} className="mb-6">
                <h3 className="font-semibold text-foreground mb-3">{category}</h3>
                <div className="space-y-3">
                  {categoryFaqs.map((faq) => (
                    <Card key={faq.id}>
                      <CardContent className="p-4">
                        {editingFaq?.id === faq.id ? (
                          <div className="space-y-3">
                            <Input
                              value={editingFaq.question}
                              onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                            />
                            <Textarea
                              value={editingFaq.answer}
                              onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                              rows={4}
                            />
                            <div className="flex gap-2">
                              <Button onClick={() => handleUpdateFaq(faq.id)} disabled={saving} size="sm">
                                <Save className="w-4 h-4 mr-1" /> Save
                              </Button>
                              <Button variant="outline" onClick={() => setEditingFaq(null)} size="sm">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-foreground">{faq.question}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{faq.answer}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setEditingFaq(faq)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteFaq(faq.id)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
          {faqs.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No FAQs yet. Create your first FAQ!</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentManager;
